import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { ui } from './Header.styled';
import { ROUTES, USER_ROLES } from '../constants';
import { selectCurrentUserIsAdmin, selectCurrentUserRole, selectUserOrgId } from '../../auth/redux/slice';
import { loadOrganization, selectOrganization } from '../../organizations/add-edit/redux/slice';
import { BillingNoticeTooltip } from '../../orders/search/components/BillingNoticeTooltip';
import { CreateOrderIntentModal } from '../../orders/search/components/CreateOrderIntentModal';

// Moved out of the Orders page's action bar so "Create New Order" is reachable from every page,
// next to the profile section in the header - same gating/behavior as before, just relocated.
// Self-contained: loads whatever org data it needs itself (loadOrganization for org-scoped users)
// rather than relying on whichever page happens to be mounted, since it can now render anywhere.
export const CreateOrderAction: React.FC<{}> = () => {
    const dispatch = useDispatch();
    const [intentModalOpen, setIntentModalOpen] = useState(false);
    const isAdmin = useSelector(selectCurrentUserIsAdmin);
    const roleName = useSelector(selectCurrentUserRole);
    // selectCurrentUserIsAdmin is also true for Organization Admin(+Provider) - they belong to a
    // single, known org (like ShipToClinic.tsx's showOrgDropdown check), so they get gated on that
    // org's own preBuyEnabled flag same as any other org-scoped role. Only a true multi-org admin
    // (who hasn't picked an org yet at this point) always sees the modal.
    const isTrueAdmin = isAdmin && roleName !== USER_ROLES.OrganizationAdminProvider && roleName !== USER_ROLES.OrganizationAdmin;
    const selectedUserOrgId = useSelector(selectUserOrgId);
    const selectedOrganization = useSelector(selectOrganization);

    // Sep 2026 - "has a payment method on file" used to be inferred purely from defaultToken,
    // which only the LEGACY (non-Stripe) processor ever populates - a Stripe org's payment method
    // lives in OrganizationStripeBilling instead, so defaultToken stays null/empty forever for
    // those orgs even after a successful Stripe setup, permanently blocking Create Order. Legacy
    // orgs (useStripeBilling false/undefined) keep using defaultToken exactly as before - see
    // CLAUDE.md/nuvia-api-spec-adjacent history for why that flow is untouched here.
    const hasPaymentMethod = selectedOrganization?.useStripeBilling
        ? !!selectedOrganization?.hasStripePaymentMethod
        : (selectedOrganization?.defaultToken !== null && selectedOrganization?.defaultToken !== '');

    useEffect(() => {
        if (selectedUserOrgId) dispatch(loadOrganization({ orgId: selectedUserOrgId }));
    }, [dispatch, selectedUserOrgId]);

    return (
        <>
            {(!selectedOrganization?.useBillingPortal || (selectedOrganization?.billingOption === 'Pre-purchase Test Subscriptions' && selectedOrganization?.availableTestCredits! > 0) || hasPaymentMethod) && !selectedOrganization?.suspend && (
                <ui.CreateOrderButton
                    to={ROUTES.CreateOrder}
                    onClick={(e) => {
                        // Only orgs with Pre-Buy enabled get the intent modal (kit-ordering options
                        // included) - admins haven't picked an org yet at this point, so they still
                        // see it too. Everyone else falls straight through to the existing order
                        // form via this button's own `to` link, same as before Pre-Buy existed.
                        if (isTrueAdmin || selectedOrganization?.preBuyEnabled) {
                            e.preventDefault();
                            setIntentModalOpen(true);
                        }
                    }}
                >
                    <AddOutlinedIcon sx={{ fontSize: '17px' }} />
                    Create Order
                </ui.CreateOrderButton>
            )}
            {!selectedOrganization?.suspend && selectedOrganization?.useBillingPortal && ((selectedOrganization?.billingOption === 'Pre-purchase Test Subscription' && selectedOrganization?.availableTestCredits! === 0) || !hasPaymentMethod) && (
                <BillingNoticeTooltip variant="missing-payment">
                    <ui.SuspendedCreateOrderButton to={ROUTES.CreateOrder} onClick={(e) => e.preventDefault()}>
                        <AddOutlinedIcon sx={{ fontSize: '17px' }} />
                        Create Order
                    </ui.SuspendedCreateOrderButton>
                </BillingNoticeTooltip>
            )}
            {selectedOrganization?.suspend && (
                <BillingNoticeTooltip variant="suspended">
                    <ui.SuspendedCreateOrderButton to={ROUTES.CreateOrder} onClick={(e) => e.preventDefault()}>
                        <AddOutlinedIcon sx={{ fontSize: '17px' }} />
                        Create Order
                    </ui.SuspendedCreateOrderButton>
                </BillingNoticeTooltip>
            )}
            <CreateOrderIntentModal open={intentModalOpen} onClose={() => setIntentModalOpen(false)} />
        </>
    );
};
